import { formatNumber } from '@/utils/numberFormatting'
import { Plus } from 'lucide-react'
import { useState } from 'react'

import { AddTargetedAreaModal } from '../AddTargetedAreaModal'
import { TargetedAreaCard } from '../TargetedAreaCard'
import { BaseCard, useCardActions } from '../ui/base-card'
import { Button } from '../ui/button'
import { ReadOnlyInput } from '../ui/readonly-input'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

import type {
	Coordinate,
	County,
	GridVersionPayload,
	Location,
	TargetingType,
} from 'honeygrid-types'
import type { FC } from 'react'

interface CountiesCardProps {
	countyIds: string[]
	counties: County[]
	onRemove: (countyId: string) => void
	onEdit: (
		oldCountyId: string,
		targetedArea: {
			type: TargetingType
			data: {
				locationId?: Location['uuid']
				radius?: number
				county?: County['id']
				zipCode?: string
			}
		}
	) => void
	locations?: Location[]
	zipCodes?: string[]
	totalTargetedAreas: number
	removeTargetedArea: (type: TargetingType, id: string) => void
}

interface ZipCodesCardProps {
	zipCodes: string[]
	onRemove: (zipCode: string) => void
	onEdit: (
		oldZipCode: string,
		targetedArea: {
			type: TargetingType
			data: {
				locationId?: Location['uuid']
				radius?: number
				county?: County['id']
				zipCode?: string
			}
		}
	) => void
	locations?: Location[]
	counties?: County[]
	totalTargetedAreas: number
}

const ZipCodesCard: FC<ZipCodesCardProps> = ({
	zipCodes,
	onRemove,
	onEdit: _onEdit,
	locations,
	counties,
	totalTargetedAreas: _totalTargetedAreas,
}) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const { createEditAction, createRemoveAction } = useCardActions()

	// Create headline
	const headline = `${zipCodes.length} ${zipCodes.length === 1 ? 'Zip code' : 'Zip codes'}`

	// Create subheadline with tooltip for overflow
	const getSubheadlineAndTooltip = () => {
		if (zipCodes.length === 0) return { subheadline: 'Unknown Zip codes', tooltipText: null }
		if (zipCodes.length === 1) {
			return {
				subheadline: zipCodes[0],
				tooltipText: null,
			}
		}
		if (zipCodes.length === 2) {
			return {
				subheadline: `${zipCodes[0]}, ${zipCodes[1]}`,
				tooltipText: null,
			}
		}

		// 3 or more zip codes - show first two + "N more" with tooltip
		const firstTwo = zipCodes.slice(0, 2)
		const remaining = zipCodes.slice(2)
		const subheadline = `${firstTwo[0]}, ${firstTwo[1]}, + ${remaining.length} more`
		const tooltipText = zipCodes.join('\n')

		return { subheadline, tooltipText }
	}

	const { subheadline, tooltipText } = getSubheadlineAndTooltip()

	// For simplicity, remove action will remove all zip codes
	const actions = [
		createEditAction(() => setIsEditModalOpen(true)),
		createRemoveAction(() => {
			// Remove all zip codes
			zipCodes.forEach((code) => onRemove(code))
		}),
	]

	const subheadlineElement = tooltipText ? (
		<p className="text-sm text-muted-foreground truncate">
			{subheadline.split(', + ')[0]}, +
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="border-b border-dotted border-current cursor-pointer hover:border-solid">
						{subheadline.split(', + ')[1]}
					</span>
				</TooltipTrigger>
				<TooltipContent side="right" className="max-w-xs whitespace-pre-line">
					{tooltipText}
				</TooltipContent>
			</Tooltip>
		</p>
	) : (
		<p className="text-sm text-muted-foreground truncate">{subheadline}</p>
	)

	return (
		<>
			<BaseCard title={headline} actions={actions} showActionsOnHover={true} className="min-w-0">
				{subheadlineElement}
			</BaseCard>
			<AddTargetedAreaModal
				open={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={(_targetedArea) => {
					// For editing, we'll just close the modal for now
					setIsEditModalOpen(false)
				}}
				locations={locations}
				counties={counties || []}
				zipCodes={zipCodes || []}
			/>
		</>
	)
}

const CountiesCard: FC<CountiesCardProps> = ({
	countyIds,
	counties,
	onRemove: _onRemove,
	onEdit: _onEdit,
	locations,
	zipCodes,
	totalTargetedAreas: _totalTargetedAreas,
	removeTargetedArea,
}) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const { createEditAction, createRemoveAction } = useCardActions()

	// Get county details and sort alphabetically
	const selectedCounties = (
		countyIds.map((id) => counties.find((c) => c.id === id)).filter(Boolean) as County[]
	).sort((a, b) => a.name.localeCompare(b.name))

	// Create headline
	const headline = `${countyIds.length} ${countyIds.length === 1 ? 'County' : 'Counties'}`

	// Create subheadline with tooltip for overflow
	const getSubheadlineAndTooltip = () => {
		if (selectedCounties.length === 0) return { subheadline: 'Unknown Counties', tooltipText: null }
		if (selectedCounties.length === 1) {
			const county = selectedCounties[0]
			return {
				subheadline: `${county.name}, ${county.state}`,
				tooltipText: null,
			}
		}
		if (selectedCounties.length === 2) {
			return {
				subheadline: `${selectedCounties[0].name}, ${selectedCounties[1].name}`,
				tooltipText: null,
			}
		}

		// 3 or more counties - show first two + "N more" with tooltip
		const firstTwo = selectedCounties.slice(0, 2)
		const remaining = selectedCounties.slice(2)
		const subheadline = `${firstTwo[0].name}, ${firstTwo[1].name}, + ${remaining.length} more`
		const tooltipText = selectedCounties.map((c) => `${c.name} (${c.state})`).join('\n')

		return { subheadline, tooltipText }
	}

	const { subheadline, tooltipText } = getSubheadlineAndTooltip()

	// Handle removing all counties (removes the entire card)
	const handleRemoveAllCounties = () => {
		// Remove all counties in a single batch operation to avoid state race conditions
		removeTargetedArea('county', 'REMOVE_ALL')
	}

	// Handle edit action - open the add targeted area modal for bulk editing
	const handleEditCounties = () => {
		setIsEditModalOpen(true)
	}

	// Create actions using the standard card action pattern
	const actions = [
		createEditAction(handleEditCounties),
		createRemoveAction(handleRemoveAllCounties),
	]

	const subheadlineElement = tooltipText ? (
		<p className="text-sm text-muted-foreground truncate">
			{subheadline.split(', + ')[0]}, +
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="border-b border-dotted border-current cursor-pointer hover:border-solid">
						{subheadline.split(', + ')[1]}
					</span>
				</TooltipTrigger>
				<TooltipContent side="right" className="max-w-xs whitespace-pre-line">
					{tooltipText}
				</TooltipContent>
			</Tooltip>
		</p>
	) : (
		<p className="text-sm text-muted-foreground truncate">{subheadline}</p>
	)

	return (
		<>
			<BaseCard title={headline} actions={actions} showActionsOnHover={true} className="min-w-0">
				{subheadlineElement}
			</BaseCard>
			<AddTargetedAreaModal
				open={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={(_targetedArea) => {
					// For editing, we'll just close the modal for now
					// The actual edit logic would depend on the specific requirements
					setIsEditModalOpen(false)
				}}
				locations={locations}
				counties={counties}
				zipCodes={zipCodes || []}
			/>
		</>
	)
}

interface GridTargetedAreasSectionProps {
	version: GridVersionPayload | null
	locations: Location[] | undefined
	counties: County[]
	zipCodes: string[]
	totalHouseholdCount: number
	removeTargetedArea: (type: TargetingType, id: Location['uuid'] | County['id']) => void
	handleEditTargetedArea: (
		oldType: TargetingType,
		oldId: Location['uuid'] | County['id'],
		targetedArea: {
			type: TargetingType
			data: {
				locationId?: Location['uuid']
				radius?: number
				county?: County['id']
				zipCode?: string
			}
		}
	) => void
	setIsAddTargetedAreaModalOpen: (open: boolean) => void
}

const GridTargetedAreasSection: FC<GridTargetedAreasSectionProps> = ({
	version,
	locations,
	counties,
	zipCodes,
	totalHouseholdCount,
	removeTargetedArea,
	handleEditTargetedArea,
	setIsAddTargetedAreaModalOpen,
}) => {
	const totalTargetedAreas =
		(version?.targeted_areas?.coordinates?.length || 0) +
		(version?.targeted_areas?.counties?.length || 0) +
		(version?.targeted_areas?.zipCodes?.length || 0)

	return (
		<div className="flex flex-col w-full min-w-0 mb-6">
			<div className="flex items-center justify-between w-full h-10 mb-3">
				<h3 className="text-base font-semibold uppercase">TARGET AREAS</h3>
				<Button
					variant="ghost"
					size="icon"
					className="cursor-pointer"
					onClick={() => setIsAddTargetedAreaModalOpen(true)}
				>
					<Plus className="h-4 w-4" />
				</Button>
			</div>
			<div className="flex flex-col space-y-3 w-full min-w-0">
				<div className="flex gap-3 w-full">
					<div className="flex-1 min-w-0">
						<ReadOnlyInput
							label="Population"
							value={`${formatNumber(totalHouseholdCount)} households`}
							subText="Calculated value"
						/>
					</div>
					<div className="w-1/3 min-w-0">
						<ReadOnlyInput
							label="Area"
							value={`${formatNumber(totalHouseholdCount * 0.28468377714072)} sq mi`}
							subText="Calculated value"
						/>
					</div>
				</div>
				{version?.targeted_areas?.coordinates?.map((coordinate: Coordinate) => (
					<TargetedAreaCard
						key={coordinate.locationId}
						type="radius"
						data={{
							locationId: coordinate.locationId,
							radius: coordinate.radius,
						}}
						onRemove={() => removeTargetedArea('radius', coordinate.locationId)}
						onEdit={(targetedArea) =>
							handleEditTargetedArea('radius', coordinate.locationId, targetedArea)
						}
						locations={locations}
						counties={counties}
						zipCodes={zipCodes}
						totalTargetedAreas={totalTargetedAreas}
					/>
				))}
				{version?.targeted_areas?.counties && version.targeted_areas.counties.length > 0 && (
					<CountiesCard
						countyIds={version.targeted_areas.counties}
						counties={counties}
						onRemove={(countyId) => removeTargetedArea('county', countyId)}
						onEdit={(oldCountyId, targetedArea) =>
							handleEditTargetedArea('county', oldCountyId, targetedArea)
						}
						locations={locations}
						zipCodes={zipCodes}
						totalTargetedAreas={totalTargetedAreas}
						removeTargetedArea={removeTargetedArea}
					/>
				)}
				{version?.targeted_areas?.zipCodes && version.targeted_areas.zipCodes.length > 0 && (
					<ZipCodesCard
						zipCodes={version.targeted_areas.zipCodes}
						onRemove={(zipCode) => removeTargetedArea('zip code', zipCode)}
						onEdit={(oldZipCode, targetedArea) =>
							handleEditTargetedArea('zip code', oldZipCode, targetedArea)
						}
						locations={locations}
						counties={counties}
						totalTargetedAreas={totalTargetedAreas}
					/>
				)}
				{!version?.targeted_areas?.coordinates?.length &&
					!version?.targeted_areas?.counties?.length &&
					!version?.targeted_areas?.zipCodes?.length && (
						<div className="mt-2 text-center">
							<p className="text-sm text-muted-foreground">No target areas added</p>
						</div>
					)}
			</div>
		</div>
	)
}

export default GridTargetedAreasSection
